<template>
  <BaseModalEditCreate
    :disable="mode != 'edit'"
    :title="isNew ? $t('common.actions.create') : $t('common.actions.edit')"
    :is-modified="isModified"
    @dismiss-changes="emit('close')"
    @save="onSave">
    <template #form>
      <BaseSelectDropdown
        :items="filteredTraderSites"
        :selected-item="selectedTradersite"
        name="tradersites"
        :mode="mode"
        size="sm"
        flat
        :label="$t('customer.traderSites')"
        show-error-as-tooltip
        :error="$v.traderSiteId.required.$invalid ? $v.traderSiteId.required.$message : ''"
        @update:selected="handleTradersiteSelection" />

      <BaseSelectDropdown
        :items="dropdownRanges"
        :selected-item="selectedRange"
        name="ranges"
        :mode="mode"
        size="sm"
        flat
        :label="$t('customer.ranges')"
        show-error-as-tooltip
        clearable
        :error="$v.rangeId.requiredIf?.$invalid ? $v.rangeId.requiredIf.$message : ''"
        @update:selected="handleRangeSelection" />

      <div class="flex gap-2 mt-4">
        <BaseCheckbox
          :mode="mode"
          v-model="modifiedData.hasDynamicRange"
          color="base"
          flat
          @update:model-value="handleDynamicRangeChange" />
        <span class="my-auto text-sm font-normal text-left text-gray-600">{{
          $t('ranges.hasDynamicRange')
        }}</span>
      </div>

      <BaseToggle
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="modifiedData.isActive" />
    </template>
  </BaseModalEditCreate>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import type { RangeCustomerTradersite } from '@/types/Ranges';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseCheckbox from '../common/BaseCheckbox.vue';
import BaseToggle from '../common/BaseToggle.vue';
import { deepCopy } from '@/utils/data';
import type { SelectDropdownItem } from '@/types/Base';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { helpers, required, requiredIf } from '@vuelidate/validators';
import { RecordMode } from '@/types/Base';
import type { TraderSite } from '@/types/Traders';
const props = defineProps<{
  isNew: boolean;
  mode: 'view' | 'edit';
  excludeTraderSites: string[];
  traderSites: TraderSite[];
  item: RangeCustomerTradersite;
}>();

const modifiedData = ref<RangeCustomerTradersite>(deepCopy<RangeCustomerTradersite>(props.item));
const isModified = ref<boolean>(false);

const emit = defineEmits(['close', 'save']);

const dropdownTraderSites = ref<SelectDropdownItem[]>([]);
const selectedTradersite = ref<SelectDropdownItem | null>(null);

const { dropdownRanges, ranges } = storeToRefs(useLookupsStore());
const { reloadSingleLookup } = useLookupsStore();

const filteredTraderSites = computed(() => {
  if (!props.excludeTraderSites || props.excludeTraderSites.length === 0) {
    return dropdownTraderSites.value;
  }
  return dropdownTraderSites.value.filter(
    (item) => !props.excludeTraderSites.includes(String(item.value))
  );
});

onMounted(async () => {
  await reloadSingleLookup('ranges', true);
  if (props.traderSites)
    dropdownTraderSites.value = props.traderSites.map((item) => ({
      value: item.id,
      name: `${item.name}, ${item.code}`,
    }));
  await initDropdowns();
});

const selectedRange = ref<SelectDropdownItem | null>(null);
function handleRangeSelection(item: SelectDropdownItem) {
  selectedRange.value = item ? item : null;
  const range = ranges.value.find((x) => x.id === item.value);
  if (modifiedData.value) {
    if (item) {
      modifiedData.value.hasDynamicRange = false;
      modifiedData.value.rangeId = item.value.toString();
      modifiedData.value.range = {
        id: item.value.toString(),
        name: item.name,
        code: range ? range.code : '',
      };
    } else {
      modifiedData.value.rangeId = '';
      if (modifiedData.value.range) {
        modifiedData.value.range.id = '';
        modifiedData.value.range.name = '';
      }
    }
  }
}

async function initDropdowns() {
  if (modifiedData.value && modifiedData.value.rangeId) {
    const rangeValue = dropdownRanges.value.find(
      (item) => item.value == modifiedData.value?.rangeId
    );
    if (rangeValue) {
      selectedRange.value = rangeValue;
      modifiedData.value.rangeId = rangeValue.value as string;
    }
  }
  if (props.item.traderSiteId) {
    const traderSiteValue = dropdownTraderSites.value.find(
      (item) => item.value == props.item?.traderSiteId
    );
    if (traderSiteValue) selectedTradersite.value = traderSiteValue;
  }
}

function handleTradersiteSelection(item: SelectDropdownItem) {
  selectedTradersite.value = item;

  if (item) {
    modifiedData.value.traderSiteId = item.value as string;
    modifiedData.value.traderSite = {
      id: String(item.value),
      name: item.name.split(',')[0],
      code: item.name.split(',')[1],
    };
  }
}

function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (modifiedData.value.range && props.isNew) {
    const range = ranges.value.find((x) => x.id == modifiedData.value.rangeId);
    if (range) modifiedData.value.range.code = range.code;
  }
  if (isModified.value && !modifiedData.value.recordMode)
    modifiedData.value.recordMode = RecordMode.Modified;
  emit('save', modifiedData.value);
}

function handleDynamicRangeChange(checked: boolean | undefined) {
  if (checked) {
    selectedRange.value = null;
    if (modifiedData.value) {
      modifiedData.value.rangeId = '';
      modifiedData.value.range = undefined;
    }
  }
}

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.rangeId = newData.rangeId || '';
    validationModel.value.traderSiteId = newData.traderSiteId || '';
    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.item);
  },
  { deep: true }
);

const { t: $t } = useI18n({ useScope: 'global' });

const validationModel = ref({
  rangeId: modifiedData.value?.rangeId || '',
  traderSiteId: modifiedData.value.traderSiteId || '',
});
const rules = computed(() => ({
  rangeId: {
    requiredIf: helpers.withMessage(
      $t('messages.errors.requiredField'),
      requiredIf(() => !modifiedData.value.hasDynamicRange)
    ),
  },
  traderSiteId: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true, $scope: false });
</script>
